import { useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useGlobalQuery } from '@/state/query-client/hooks';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { useToast } from '@/hooks/use-toast';
import { useAuthStore } from '@/state/store/auth';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import graphqlClient from '@/graphql/graphql-client';
import {
  requestOrgCompanies,
  requestOrgCompaniesByUser,
  type CompanyItem,
} from '@/graphql/queries/companies';
import {
  DELETE_COMPANY_MUTATION,
  INSERT_COMPANY_MUTATION,
  UPDATE_COMPANY_MUTATION,
} from '@/graphql/mutations/companies';
import type { Company, CompanyInsertInput, CompanyUpdateInput } from '../types/companies.types';

const QUERY_KEY = 'companies';

const toCompany = (
  company: CompanyItem,
  ctx: { userId: string | null; isOrgAdmin: boolean }
): Company => {
  const scope = company.Scope === 'organization' ? 'organization' : 'user';
  const canManage =
    scope === 'user'
      ? company.CreatedBy === ctx.userId
      : ctx.isOrgAdmin || company.CreatedBy === ctx.userId;
  return {
    id: company.ItemId,
    createdBy: company.CreatedBy ?? undefined,
    scope,
    name: company.Name ?? '',
    website: company.Website ?? '',
    address: company.Address ?? '',
    email: company.Email ?? '',
    phone: company.Phone ?? '',
    organizationId: company.OrganizationId ?? undefined,
    canManage,
  };
};

const itemsOf = (items: Array<CompanyItem | null> | null | undefined): CompanyItem[] =>
  (items ?? []).filter((item): item is CompanyItem => !!item);

export const useGetCompanies = (enabled = true) => {
  const userId = useAuthStore((s) => s.userId);
  const orgId = useCurrentOrgId();
  const isOrgAdmin = useAuthStore((s) => s.activeOrgRole === 'org_admin');

  return useGlobalQuery({
    queryKey: [QUERY_KEY, orgId, userId],
    queryFn: async () => {
      const queries: Array<Promise<CompanyItem[]>> = [];
      if (userId) {
        queries.push(requestOrgCompaniesByUser(userId).then((res) => itemsOf(res.getOrgCompanys?.items)));
      }
      if (orgId) {
        queries.push(requestOrgCompanies(orgId).then((res) => itemsOf(res.getOrgCompanys?.items)));
      }
      const results = await Promise.all(queries);
      const byId = new Map<string, CompanyItem>();
      for (const list of results) for (const company of list) byId.set(company.ItemId, company);
      return Array.from(byId.values())
        .map((company) => toCompany(company, { userId, isOrgAdmin }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: enabled && (!!userId || !!orgId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useCompanyLookup = (enabled = true) => {
  const { data, isLoading } = useGetCompanies(enabled);

  const byId = useMemo(
    () => new Map((data ?? []).map((company) => [company.id, company])),
    [data]
  );

  return { byId, isLoading };
};

export const useCreateCompany = () => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: async (input: CompanyInsertInput) => {
      const res = await graphqlClient.request(INSERT_COMPANY_MUTATION, input);
      return res.insertOrgCompany?.itemId ?? '';
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to save company' });
    },
  });
};

export const useUpdateCompany = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: ({ itemId, input }: { itemId: string; input: CompanyUpdateInput }) =>
      graphqlClient.request(UPDATE_COMPANY_MUTATION, { ItemId: itemId, ...input }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({ variant: 'success', title: 'Company updated.' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to update company' });
    },
  });
};

export const useDeleteCompany = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useMutation({
    mutationFn: (itemId: string) =>
      graphqlClient.request(DELETE_COMPANY_MUTATION, { ItemId: itemId, isHardDelete: true }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
      toast({ variant: 'success', title: 'Company deleted.' });
    },
    onError: (error: Error) => {
      handleError(error, { title: 'Failed to delete company' });
    },
  });
};
